import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { useApplicationListSync } from '@/hooks/useApplicationListSync';
import { useLanguage } from '@/contexts/LanguageContext';
import { toast } from 'sonner';
import type { Application, ScreeningReport, CandidateStructuredData, PipelineDetailRun, AiConversationState, AiChatContext, AiCandidateChatContext } from '@/types';
import { useAiChatStore, isSessionExpired, EMPTY_SESSION } from '@/stores/aiChatStore';
import { useGlobalLoading } from '@/stores/loadingStore';
import { emitBillingUsageChanged } from '@/lib/billingEvents';
import {
  isRecentSettledListRow,
  persistRecentSettledApplications,
} from '@/lib/recentSettledApplications';

/**
 * buildCandidateContext — constructs AiCandidateChatContext from data already in memory.
 * Called only on the first chat turn (conversationState === null).
 * Returns null if no meaningful data is available.
 */
function buildCandidateContext(
  app: Application,
  reports: ScreeningReport[],
  structuredData: CandidateStructuredData | null | undefined,
): AiCandidateChatContext | null {
  const latestReport = reports.length > 0 ? reports[reports.length - 1] : null;

  const candidateContext: AiCandidateChatContext = {
    // Always include path IDs so tools can call Firestore without asking the recruiter
    positionId: app.positionId,
    companyId: app.companyId,
    applicationId: app.id,
  };

  // Candidate info from structured data
  if (structuredData) {
    candidateContext.candidate = {
      firstName: app.candidateFirstName,
      lastName: app.candidateLastName,
      skills: structuredData.skills ?? [],
      experience: (structuredData.experience ?? []).map(e => ({
        role: e.role,
        company: e.company,
        startDate: e.startDate,
        endDate: e.endDate,
      })),
      education: (structuredData.education ?? []).map(e => ({
        institution: e.institution,
        degree: e.degree,
        field: e.field,
      })),
      currentRole: structuredData.currentRole,
      currentCompany: structuredData.currentCompany,
      yearsOfExperience: structuredData.yearsOfExperience,
      summary: structuredData.summary,
    };
  } else {
    // Fallback: use only snapshot data (no structured CV)
    candidateContext.candidate = {
      firstName: app.candidateFirstName,
      lastName: app.candidateLastName,
      skills: [],
      experience: [],
      education: [],
    };
  }

  // Screening report
  if (latestReport) {
    candidateContext.screening = {
      overallScore: latestReport.overallScore,
      summary: latestReport.summary,
      strengths: latestReport.strengths ?? [],
      weaknesses: latestReport.weaknesses ?? [],
      suggestedQuestions: latestReport.suggestedQuestions ?? [],
    };
  }

  // Application state
  candidateContext.application = {
    state: app.state,
    appliedAt: app.appliedAt,
    channel: app.channel,
  };

  return candidateContext;
}

export interface UseApplicationDetailReturn {
  app: Application | null;
  /**
   * Sprint 7+ follow-up (21/05/26) — set to `true` once the lookup
   * (fast path or legacy scan) has finished without finding the
   * application. Detail page uses it to render an explicit
   * "not found" empty state instead of sitting on a perpetual
   * skeleton, which was the symptom recruiters reported as
   * "loader infinito" when the bare /applications/:appId route
   * was hit and the legacy scan failed (capped position list,
   * recently soft-deleted position, etc.).
   */
  notFound: boolean;
  positionAssignedMembers: string[];
  screeningReports: ScreeningReport[];
  cvContent: string | null | undefined;
  structuredData: CandidateStructuredData | null | undefined;
  rawScreeningReports: ScreeningReport[] | undefined;
  pipelineDetail: PipelineDetailRun[];
  companyId: string;
  isAwaiting: boolean;
  canReject: boolean;
  canHire: boolean;
  canWithdraw: boolean;
  canReactivate: boolean;
  hasInterviews: boolean;
  documents: { id: string; name: string; type: string; size: string; uploadedAt: string }[];
  // Reject
  rejectOpen: boolean;
  setRejectOpen: (v: boolean) => void;
  rejectNote: string;
  setRejectNote: (v: string) => void;
  // Sprint 5 (S5.B): opt-in to send the candidate-facing rejection email.
  // Defaults to true so the polite-rejection flow is the path of least
  // resistance. Recruiters who reject silently uncheck the box.
  rejectSendEmail: boolean;
  setRejectSendEmail: (v: boolean) => void;
  handleReject: () => Promise<void>;
  rejectBusy: boolean;
  // Hire
  hireOpen: boolean;
  setHireOpen: (v: boolean) => void;
  hireNote: string;
  setHireNote: (v: string) => void;
  handleHire: () => Promise<void>;
  hireBusy: boolean;
  // Sprint 5 (S5.G) — withdraw confirmation modal (mirrors reject/hire UX).
  // Previously the withdraw action fired immediately on dropdown click
  // which led to accidental withdrawals on touch devices.
  withdrawOpen: boolean;
  setWithdrawOpen: (v: boolean) => void;
  withdrawNote: string;
  setWithdrawNote: (v: string) => void;
  reactivateOpen: boolean;
  setReactivateOpen: (v: boolean) => void;
  reactivateNote: string;
  setReactivateNote: (v: string) => void;
  handleReactivate: () => Promise<void>;
  // Chat
  chatOpen: boolean;
  setChatOpen: (v: boolean) => void;
  chatMessages: { role: 'user' | 'ai'; text: string }[];
  chatInput: string;
  setChatInput: (v: string) => void;
  chatLoading: boolean;
  chatStreamStatuses: string[];
  sendChat: () => Promise<void>;
  resetChat: () => void;
  conversationState: AiConversationState | null;
  // Interview
  interviewOpen: boolean;
  setInterviewOpen: (v: boolean) => void;
  onInterviewScheduled: () => void;
  patchApplicationInListCaches: (application: Application) => void;
  // Actions
  handleDownloadResume: () => Promise<void>;
  handleDownloadDocument: (docId: string, docName: string) => Promise<void>;
  handleViewResume: () => Promise<void>;
  handleWithdraw: () => Promise<void>;
  withdrawBusy: boolean;
  setApp: React.Dispatch<React.SetStateAction<Application | null>>;
}

export function useApplicationDetail(appId: string | undefined, posId?: string): UseApplicationDetailReturn {
  const { selectedCompany } = useAuth();
  const { t } = useLanguage();
  const listSync = useApplicationListSync();

  const syncApplicationToListCache = useCallback((application: Application) => {
    listSync.patchMany(application.companyId, [application], false);
    if (isRecentSettledListRow(application)) {
      persistRecentSettledApplications(application.companyId, [application]);
    }
  }, [listSync]);

  const [app, setApp] = useState<Application | null>(null);
  // Sprint 7+ follow-up (21/05/26): notFound flips to true after the
  // resolver pipeline (fast path / legacy scan) terminates without a
  // hit. The page component branches on this so the user sees a real
  // empty state instead of the loading skeleton.
  const [notFound, setNotFound] = useState(false);
  const [positionAssignedMembers, setPositionAssignedMembers] = useState<string[]>([]);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectNote, setRejectNote] = useState('');
  // Sprint 5 (S5.B) — checkbox state for the rejection email opt-in.
  const [rejectSendEmail, setRejectSendEmail] = useState(false);
  const [hireOpen, setHireOpen] = useState(false);
  const [hireNote, setHireNote] = useState('');
  const [rejectBusy, setRejectBusy] = useState(false);
  const [hireBusy, setHireBusy] = useState(false);
  const [withdrawBusy, setWithdrawBusy] = useState(false);
  // Sprint 5 (S5.G)
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [withdrawNote, setWithdrawNote] = useState('');
  const [reactivateOpen, setReactivateOpen] = useState(false);
  const [reactivateNote, setReactivateNote] = useState('');
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatStreamStatuses, setChatStreamStatuses] = useState<string[]>([]);
  const [chatStreamingText, setChatStreamingText] = useState('');
  const chatGenRef = useRef(0);
  const chatAbortRef = useRef<AbortController | null>(null);

  // Persistent chat session — keyed by applicationId, survives refresh/navigation.
  // Session auto-resets after AI_CHAT_CONFIG.SESSION_TIMEOUT_HOURS of inactivity.
  const sessionKey = appId ? `candidate_${appId}` : null;
  const setCandidateSession = useAiChatStore(s => s.setCandidateSession);
  const clearCandidateSession = useAiChatStore(s => s.clearCandidateSession);

  // Direct reactive selector — re-renders when store hydrates from localStorage on refresh
  const rawSession = useAiChatStore(s => sessionKey ? s.sessions[sessionKey] ?? null : null);
  const chatSession = rawSession && !isSessionExpired(rawSession.lastInteractionAt) ? rawSession : null;
  const chatMessages = chatSession?.messages ?? [];
  const renderedChatMessages = chatStreamingText
    ? chatMessages.map((msg, index) => (
        index === chatMessages.length - 1 && msg.role === 'ai'
          ? { ...msg, text: chatStreamingText }
          : msg
      ))
    : chatMessages;
  const conversationState = chatSession?.conversationState ?? null;
  const chatOpen = chatSession?.isOpen ?? false;

  const setChatOpen = useCallback((v: boolean) => {
    // Fix 1: guard against null sessionKey (e.g. appId not yet resolved from URL)
    if (!sessionKey) {
      if (import.meta.env.DEV) console.warn('[useApplicationDetail] setChatOpen called before sessionKey is ready');
      return;
    }
    // Fix 2: if opening and the stored session is expired (or missing), initialize a
    // fresh session so the reactive selector picks it up correctly instead of silently
    // writing isOpen onto a stale entry that gets discarded on read.
    if (v && (!rawSession || isSessionExpired(rawSession.lastInteractionAt))) {
      setCandidateSession(sessionKey, { ...EMPTY_SESSION, isOpen: true });
    } else {
      setCandidateSession(sessionKey, { isOpen: v });
    }
  }, [sessionKey, rawSession, setCandidateSession]);
  const [interviewOpen, setInterviewOpen] = useState(false);

  const [rawScreeningReports, setScreeningReports] = useState<ScreeningReport[] | undefined>(undefined);
  const [cvContent, setCvContent] = useState<string | null | undefined>(undefined);
  const [structuredData, setStructuredData] = useState<CandidateStructuredData | null | undefined>(undefined);
  const [apiDocuments, setApiDocuments] = useState<{ id: string; name: string; type: string; size: string; uploadedAt: string }[] | null>(null);
  const [pipelineDetail, setPipelineDetail] = useState<PipelineDetailRun[]>([]);

  // Sprint 7+ follow-up (21/05/26): publish to the global loading bar
  // while we resolve the application + load its supporting documents.
  // The detail page used to render its own `<TopLoadingBar active={true}>`
  // inside the skeleton — works, but the bar then disappeared the moment
  // the app entity arrived even if pipeline details were still streaming.
  // The global bar gives a consistent indication across the whole shell.
  const isLoadingDetail = !app && !notFound;
  useGlobalLoading(isLoadingDetail, 'application-detail');

  useEffect(() => {
    if (!appId || !selectedCompany) return;
    const companyId = selectedCompany.id;
    // Reset state on every navigation so the previous candidate's data
    // never bleeds into the new view (e.g. notFound from a prior failed
    // lookup).
    setNotFound(false);
    let cancelled = false;

    if (posId) {
      // Fast path — positionId already known from URL params, single
      // direct fetch.
      Promise.all([
        api.applications.get(companyId, posId, appId),
        api.positions.get(companyId, posId).catch(() => null),
      ])
        .then(([foundApp, pos]) => {
          if (cancelled) return;
          if (foundApp) {
            const merged = { ...foundApp, positionTitle: foundApp.positionTitle || pos?.title || '' };
            setApp(merged);
            syncApplicationToListCache(merged);
            loadFiles(companyId, posId, appId);
            if (pos) setPositionAssignedMembers(pos.assignedMembers || []);
          } else {
            // 404 / withdrawn / wrong tenant — surface it instead of
            // silently spinning.
            setNotFound(true);
          }
        })
        .catch(() => {
          if (!cancelled) setNotFound(true);
        });
      return () => { cancelled = true; };
    }

    // Sprint 7+ follow-up (21/05/26): rewritten legacy scan.
    //
    // The previous loop awaited `api.applications.get` for every
    // position serially. With N=20 positions and ~150ms per fetch the
    // user waited 3 seconds for nothing — and once positions exceeded
    // the 50-doc list cap the apartment was simply unreachable, so the
    // page sat on the skeleton forever. Recruiters reported this as
    // "loader infinito" after clicking a candidate name from the
    // company-wide Applications listing.
    //
    // New behaviour:
    //   1. Fire all `applications.get` calls in parallel.
    //   2. Pick the first non-undefined response.
    //   3. If none resolved, flip notFound=true so the detail page
    //      renders an empty state with a "back" CTA instead of an
    //      eternal skeleton.
    //   4. The cancelled flag prevents stale writes when the user
    //      navigates away mid-scan.
    api.positions.list(companyId)
      .then(async positions => {
        if (cancelled) return;
        if (positions.length === 0) {
          setNotFound(true);
          return;
        }
        const results = await Promise.all(
          positions.map(pos =>
            api.applications.get(companyId, pos.id, appId)
              .then(foundApp => (foundApp ? { foundApp, pos } : null))
              .catch(() => null),
          ),
        );
        if (cancelled) return;
        const hit = results.find(r => r !== null);
        if (hit) {
          const { foundApp, pos } = hit;
          const merged = { ...foundApp, positionTitle: foundApp.positionTitle || pos.title };
          setApp(merged);
          syncApplicationToListCache(merged);
          setPositionAssignedMembers(pos.assignedMembers || []);
          loadFiles(companyId, pos.id, appId);
        } else {
          setNotFound(true);
        }
      })
      .catch(() => {
        if (!cancelled) setNotFound(true);
      });

    return () => { cancelled = true; };
  }, [appId, posId, selectedCompany, syncApplicationToListCache]);

  const loadFiles = (companyId: string, positionId: string, applicationId: string) => {
    api.applications.getScreeningReports(companyId, positionId, applicationId).then(r => setScreeningReports(r));
    api.applications.getCvContent(companyId, positionId, applicationId).then(c => setCvContent(c));
    api.applications.getStructuredData(companyId, positionId, applicationId).then(d => setStructuredData(d));
    api.applications.getPipelineDetail(companyId, positionId, applicationId).then(runs => setPipelineDetail(runs));
    api.applications.listDocuments(companyId, positionId, applicationId).then(docs => {
      // We need the candidate name for display — read it from the app state if available.
      // The closure captures the app state at call time; if null, we use the raw fileName.
      setApiDocuments(docs.map(d => ({
        id:         d.id,
        name:       d.fileName,  // raw name stored; overridden with friendly name after app loads
        type:       d.type,
        size:       '—',
        uploadedAt: d.createdAt?._seconds
          ? new Date(d.createdAt._seconds * 1000).toISOString()
          : (d.createdAt as string) ?? '',
      })));
    }).catch(() => {});
  };

  const companyId = app?.companyId || selectedCompany?.id || '';
  // positionId: prefer value returned by backend (injected in getApplication response),
  // fallback to URL param (posId) — covers both fast-path and legacy scan navigation.
  const positionId = app?.positionId || posId || '';

  const reports: ScreeningReport[] = rawScreeningReports !== undefined
    ? rawScreeningReports
    : (app?.screeningReports || []);

  const isAwaiting = app?.state === 'AWAITING_DECISION';
  const canReject = app ? ['AWAITING_DECISION', 'APPLIED_NEW', 'PIPELINE_ERROR'].includes(app.state) : false;
  const canHire = canReject;
  const canWithdraw = app ? !['REJECTED', 'HIRED', 'WITHDRAWN', 'OFFER_DECLINED'].includes(app.state) : false;
  const canReactivate = app ? ['REJECTED', 'HIRED', 'WITHDRAWN', 'OFFER_DECLINED'].includes(app.state) : false;
  // Show the Interview tab whenever there is at least one row (active or cancelled), or the
  // backend says a non-cancelled interview exists (flag can be false when only cancelled remain).
  const hasInterviews = app
    ? app.interviews.length > 0 ||
      (typeof app.hasScheduledInterview === 'boolean' && app.hasScheduledInterview)
    : false;

  // Build a friendly candidate slug for document display names
  const candidateSlug = app
    ? `${app.candidateFirstName}_${app.candidateLastName}`.toLowerCase().replace(/\s+/g, '_')
    : '';

  // Use real documents from API when available, fallback to static derivation
  const documents: { id: string; name: string; type: string; size: string; uploadedAt: string }[] = (apiDocuments
    ? apiDocuments.map(d => ({
        ...d,
        name: (() => {
          if (!candidateSlug) return d.name;
          switch (d.type) {
            case 'report_screening':  return `report_screening_${candidateSlug}.json`;
            case 'resume_structured': return `resume_structured_${candidateSlug}.json`;
            case 'resume_content':    return `resume_content_${candidateSlug}.md`;
            default:                  return d.name; // resume_document keeps its original filename
          }
        })(),
      }))
    : null) ?? (() => {
    if (!app) return [];
    const latestReport = reports.length > 0 ? reports[reports.length - 1] : null;
    const hasCoverLetter = !!(
      app.collectedData?.cover_letter?.value ||
      app.collectedData?.cover_letter_url?.value ||
      app.collectedData?.cover_letter_text?.value
    );
    return [
      { id: 'doc-1', name: `${app.candidateFirstName}_${app.candidateLastName}_Resume.pdf`, type: t('appDetail.tabResume'), size: '—', uploadedAt: app.appliedAt },
      ...(hasCoverLetter ? [{ id: 'doc-2', name: 'cover_letter.pdf', type: 'Cover Letter', size: '—', uploadedAt: app.appliedAt }] : []),
      ...(latestReport ? [{ id: 'doc-3', name: `screening_report_v${latestReport.version}.json`, type: 'Screening Report', size: '—', uploadedAt: latestReport.generatedAt }] : []),
    ];
  })();

  // Clear input when chat is closed (messages + conversationState are persisted in store)
  useEffect(() => {
    if (!chatOpen) {
      setChatInput('');
    }
  }, [chatOpen]);

  const resetChat = useCallback(() => {
    chatAbortRef.current?.abort();
    chatGenRef.current++;
    setChatLoading(false);
    setChatStreamStatuses([]);
    setChatStreamingText('');
    if (sessionKey) setCandidateSession(sessionKey, { messages: [], conversationState: null, lastInteractionAt: null });
    setChatInput('');
  }, [sessionKey, setCandidateSession]);

  const sendChat = useCallback(async () => {
    if (!chatInput.trim() || !selectedCompany || !app || !sessionKey) return;

    // Abort any in-flight stream before starting a new one
    chatAbortRef.current?.abort();
    chatAbortRef.current = new AbortController();

    const gen = ++chatGenRef.current;
    const userMessage = chatInput.trim();
    const now = new Date().toISOString();

    // Append user message + AI placeholder (empty text, replaced on final)
    setCandidateSession(sessionKey, {
      messages: [...chatMessages, { role: 'user', text: userMessage }, { role: 'ai', text: '' }],
      lastInteractionAt: now,
    });
    setChatInput('');
    setChatLoading(true);
    setChatStreamStatuses([]);
    setChatStreamingText('');

    // Path IDs are sent on EVERY turn so the BE can always call tools without asking the recruiter.
    // Full candidateContext (screening, CV, etc.) is sent only on the first turn — on subsequent
    // turns it's already in the conversation history and re-sending it would waste tokens.
    const pathIds: AiCandidateChatContext = {
      positionId: app.positionId,
      companyId: app.companyId,
      applicationId: app.id,
    };

    let context: AiChatContext;
    if (!conversationState) {
      const candidateContext = buildCandidateContext(app, reports, structuredData);
      context = { candidateContext: candidateContext ?? pathIds };
    } else {
      context = { candidateContext: pathIds };
    }

    let streamedText = '';

    try {
      await api.ai.chatStream(
        selectedCompany.id,
        {
          profileName: 'candidate_chatbot',
          message: userMessage,
          conversationState: conversationState ?? undefined,
          context,
        },
        {
          onStatus: (msg) => {
            if (gen !== chatGenRef.current) return;
            setChatStreamStatuses(prev => prev[prev.length - 1] === msg ? prev : [...prev, msg]);
          },
          onChunk: (chunk) => {
            if (gen !== chatGenRef.current) return;
            streamedText += chunk;
            setChatStreamingText(streamedText);
          },
          onFinal: (response) => {
            if (gen !== chatGenRef.current) return;
            const aiNow = new Date().toISOString();
            setCandidateSession(sessionKey, {
              messages: [...chatMessages, { role: 'user', text: userMessage }, { role: 'ai', text: response.text }],
              conversationState: response.conversationState,
              lastInteractionAt: aiNow,
            });
            setChatStreamStatuses([]);
            setChatStreamingText('');
            setChatLoading(false);
            emitBillingUsageChanged();
          },
          onError: (errMsg) => {
            if (gen !== chatGenRef.current) return;
            const errorText = errMsg.includes('not authorized') || errMsg.includes('402')
              ? t('billing.aiNotEnabled')
              : 'Something went wrong. Please try again.';
            setCandidateSession(sessionKey, {
              messages: [...chatMessages, { role: 'user', text: userMessage }, { role: 'ai', text: errorText }],
              lastInteractionAt: new Date().toISOString(),
            });
            setChatStreamStatuses([]);
            setChatStreamingText('');
            setChatLoading(false);
          },
        },
        chatAbortRef.current.signal,
      );
    } catch (err: unknown) {
      if (gen !== chatGenRef.current) return;
      const apiErr = err as { status?: number; message?: string };
      const errorText = apiErr?.status === 402
        ? t('billing.aiNotEnabled')
        : (apiErr?.message ?? '').includes('demo')
        ? 'AI features are not available in demo mode.'
        : 'Something went wrong. Please try again.';
      setCandidateSession(sessionKey, {
        messages: [...chatMessages, { role: 'user', text: userMessage }, { role: 'ai', text: errorText }],
        lastInteractionAt: new Date().toISOString(),
      });
      setChatStreamStatuses([]);
      setChatStreamingText('');
      setChatLoading(false);
    }
  }, [chatInput, selectedCompany, app, conversationState, reports, structuredData, sessionKey, chatMessages, setCandidateSession, t]);

  const handleReject = useCallback(async () => {
    if (!app || rejectBusy) return;
    setRejectBusy(true);
    try {
      await api.applications.reject(companyId, positionId, app.id, rejectNote || undefined, rejectSendEmail);
      const now = new Date().toISOString();
      const newHistoryEntry = {
        id: `h-${Date.now()}`,
        fromState: app.state,
        toState: 'REJECTED' as const,
        event: 'application.rejected',
        timestamp: now,
        triggeredBy: 'You',
        note: rejectNote || undefined,
      };
      const updated: Application = {
        ...app,
        state: 'REJECTED',
        updatedAt: now,
        history: [...app.history, newHistoryEntry],
      };
      setApp(updated);
      syncApplicationToListCache(updated);
      setRejectOpen(false);
      setRejectNote('');
      setRejectSendEmail(false);
      toast.success(t('appDetail.candidateRejected'));
    } catch {
      toast.error(t('appDetail.rejectFailed'));
    } finally {
      setRejectBusy(false);
    }
  }, [app, companyId, positionId, rejectNote, rejectSendEmail, rejectBusy, syncApplicationToListCache, t]);

  const handleHire = useCallback(async () => {
    if (!app || hireBusy) return;
    setHireBusy(true);
    try {
      await api.applications.hire(companyId, positionId, app.id, hireNote || undefined);
      const now = new Date().toISOString();
      const newHistoryEntry = {
        id: `h-${Date.now()}`,
        fromState: app.state,
        toState: 'HIRED' as const,
        event: 'application.hired',
        timestamp: now,
        triggeredBy: 'You',
        note: hireNote || undefined,
      };
      const updated: Application = {
        ...app,
        state: 'HIRED',
        updatedAt: now,
        history: [...app.history, newHistoryEntry],
      };
      setApp(updated);
      syncApplicationToListCache(updated);
      setHireOpen(false);
      setHireNote('');
      toast.success(t('appDetail.candidateHired'));
    } catch {
      toast.error(t('appDetail.hireFailed'));
    } finally {
      setHireBusy(false);
    }
  }, [app, companyId, positionId, hireNote, hireBusy, syncApplicationToListCache, t]);

  const handleWithdraw = useCallback(async () => {
    if (!app || withdrawBusy) return;
    setWithdrawBusy(true);
    try {
      const updated = await api.applications.withdraw(companyId, positionId, app.id, withdrawNote.trim() || undefined);
      setApp(updated);
      syncApplicationToListCache(updated);
      setWithdrawOpen(false);
      setWithdrawNote('');
      toast.success(t('appDetail.candidateWithdrawn'));
    } catch {
      toast.error(t('appDetail.withdrawFailed'));
    } finally {
      setWithdrawBusy(false);
    }
  }, [app, companyId, positionId, withdrawNote, withdrawBusy, syncApplicationToListCache, t]);

  const handleReactivate = useCallback(async () => {
    if (!app) return;
    try {
      const updated = await api.applications.reactivate(
        companyId,
        positionId,
        app.id,
        reactivateNote || undefined,
      );
      setApp(updated);
      syncApplicationToListCache(updated);
      setReactivateOpen(false);
      setReactivateNote('');
      toast.success(t('appDetail.candidateReactivated'));
    } catch {
      toast.error(t('appDetail.reactivateFailed'));
    }
  }, [app, companyId, positionId, reactivateNote, syncApplicationToListCache, t]);

  const handleDownloadResume = useCallback(async () => {
    if (!app) return;
    try {
      const blob = await api.applications.downloadCvPdf(companyId, positionId, app.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${app.candidateFirstName}_${app.candidateLastName}_Resume.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t('appDetail.resumeDownloadFailed'));
    }
  }, [app, companyId, positionId, t]);

  const handleDownloadDocument = useCallback(async (docId: string, docName: string) => {
    if (!app) return;
    try {
      const blob = await api.applications.downloadDocumentById(companyId, positionId, app.id, docId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = docName;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t('appDetail.resumeDownloadFailed'));
    }
  }, [app, companyId, positionId, t]);

  const handleViewResume = useCallback(async () => {
    if (!app) return;
    try {
      const docs = await api.applications.listDocuments(companyId, positionId, app.id);
      const resumeDoc = docs.find(d => d.type === 'resume_document');
      if (resumeDoc?.downloadUrl) {
        // downloadUrl is a relative path — build the full API URL via a blob download
        const blob = await api.applications.downloadDocumentById(companyId, positionId, app.id, resumeDoc.id);
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 10000);
      } else {
        toast.info(t('appDetail.noResume'));
      }
    } catch {
      toast.error(t('appDetail.resumeDownloadFailed'));
    }
  }, [app, companyId, positionId, t]);

  const onInterviewScheduled = useCallback(() => {
    if (!app || !selectedCompany) return;
    Promise.all([
      api.applications.get(companyId, positionId, app.id),
      api.applications.getPipelineDetail(companyId, positionId, app.id),
    ]).then(([updated, runs]) => {
      if (updated) {
        setApp(updated);
        syncApplicationToListCache(updated);
      }
      setPipelineDetail(runs);
    }).catch(() => {});
  }, [app, companyId, positionId, selectedCompany, syncApplicationToListCache]);

  return {
    app,
    patchApplicationInListCaches: syncApplicationToListCache,
    notFound,
    positionAssignedMembers,
    screeningReports: reports,
    cvContent,
    canWithdraw,
    canReactivate,
    structuredData,
    rawScreeningReports,
    pipelineDetail,
    companyId,
    isAwaiting,
    canReject,
    canHire,
    hasInterviews,
    documents,
    rejectOpen,
    setRejectOpen,
    rejectNote,
    setRejectNote,
    rejectSendEmail,
    setRejectSendEmail,
    handleReject,
    rejectBusy,
    hireOpen,
    setHireOpen,
    hireNote,
    setHireNote,
    handleHire,
    hireBusy,
    handleDownloadDocument,
    chatOpen,
    setChatOpen,
    chatMessages: renderedChatMessages,
    chatInput,
    setChatInput,
    chatLoading,
    chatStreamStatuses,
    sendChat,
    resetChat,
    conversationState,
    interviewOpen,
    setInterviewOpen,
    onInterviewScheduled,
    handleDownloadResume,
    handleViewResume,
    handleWithdraw,
    withdrawBusy,
    withdrawOpen,
    setWithdrawOpen,
    withdrawNote,
    setWithdrawNote,
    reactivateOpen,
    setReactivateOpen,
    reactivateNote,
    setReactivateNote,
    handleReactivate,
    setApp,
  };
}
